import React from "react";
import Close from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { useHasRole } from "@homesusa/auth";
import {
  Format,
  Notice,
  RoleUser,
  RoleUserLabel,
  getOptionsFromEnumAndMap
} from "@homesusa/core";
import {
  FormProvider,
  Input,
  EmailInput,
  PhoneInput,
  Select
} from "@homesusa/form";
import { SubmitButton } from "@homesusa/layout";
import {
  initialNewUserValues,
  newUserRules,
  useNewUserForm
} from "../../hooks/use-new-user-form";
import { newUserModalStyles as styles } from "./new-user-modal.styles";

interface NewUserModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function NewUserModalContent({
  onClose,
  onCreated
}: Omit<NewUserModalProps, "open">): JSX.Element {
  const { isMlsAdministrator } = useHasRole();
  const titleId = React.useId();
  const {
    saving,
    confirmDiscard,
    error,
    canSubmit,
    requestClose,
    keepEditing,
    submit
  } = useNewUserForm({ onClose, onCreated });
  const options = isMlsAdministrator
    ? getOptionsFromEnumAndMap(Object.values(RoleUser), RoleUserLabel)
    : { [RoleUser.User]: RoleUserLabel.get(RoleUser.User) ?? RoleUser.User };
  const disabled = saving || confirmDiscard;

  return (
    <Dialog
      open
      onClose={requestClose}
      maxWidth="md"
      aria-labelledby={titleId}
      sx={styles.dialog}
    >
      <DialogTitle id={titleId}>
        New user
        <IconButton
          aria-label="Close new user"
          onClick={requestClose}
          disabled={saving}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Box
        component="form"
        noValidate
        sx={styles.form}
        aria-busy={saving}
        onSubmit={(event: React.FormEvent): void => {
          event.preventDefault();
        }}
      >
        <DialogContent sx={styles.content}>
          {confirmDiscard ? (
            <Alert severity="warning">
              Discard your changes? The user has not been created.
            </Alert>
          ) : error ? (
            <Box role="alert">
              <Notice variant="alert">{error}</Notice>
            </Box>
          ) : null}
          <Box sx={styles.fields}>
            <Input name="firstName" validateOn="blur" disabled={disabled} />
            <Input name="lastName" validateOn="blur" disabled={disabled} />
            <EmailInput name="email" validateOn="blur" disabled={disabled} />
            <PhoneInput
              name="businessPhone"
              validateOn="blur"
              disabled={disabled}
              placeholder={Format.PhoneNumber("0000000000")}
            />
            <PhoneInput
              name="mobilePhone"
              validateOn="blur"
              disabled={disabled}
              placeholder={Format.PhoneNumber("0000000000")}
            />
            <Select
              name="role"
              options={options}
              validateOn="blur"
              disabled={disabled || Object.keys(options).length === 1}
              removeBlankOption
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {confirmDiscard ? (
            <>
              <Button variant="outlined" onClick={keepEditing} autoFocus>
                Keep editing
              </Button>
              <Button variant="contained" color="error" onClick={onClose}>
                Discard changes
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={requestClose}
                disabled={saving}
              >
                Cancel
              </Button>
              <SubmitButton
                onClick={submit}
                buttonProps={{
                  disabled: !canSubmit || saving,
                  type: "submit",
                  sx: styles.submitButton
                }}
              >
                Create user
              </SubmitButton>
            </>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export function NewUserModal({
  open,
  ...props
}: NewUserModalProps): JSX.Element | null {
  if (!open) return null;
  return (
    <FormProvider
      initialFormState={initialNewUserValues}
      initialFormRules={newUserRules}
    >
      <NewUserModalContent {...props} />
    </FormProvider>
  );
}
